/* =====================================================================
   Owl + Fox — Collection v2.0 JavaScript
   Handles:
   - Alpine.js component `collV2()`: filter form AJAX, drawer, sort
   - Section Rendering API: no-reload filter updates + URL pushState
   - Colour ΔE (CIEDE2000): closest-colour mode
   ===================================================================== */

/* ── Section ID for Shopify Section Rendering API ── */
const COLL_SECTION_ID = 'main-collection';
const COLL_RESULTS_ID = 'coll-results';
const COLL_COUNT_ID   = 'coll-result-count';

/* ── Debounce helper ── */
function debounce(fn, ms) {
  let t;
  return function(...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), ms);
  };
}

/* ================================================================
   ALPINE COMPONENT: collV2
   ================================================================ */
function collV2() {
  return {
    loading: false,
    drawerOpen: false,
    openFilter: null, /* which horizontal filter dropdown is open */

    toggleFilter(name) {
      this.openFilter = this.openFilter === name ? null : name;
    },

    /* Called on any filter form change (checkbox, toggle, hidden input) */
    onFilterChange(e) {
      /* Price fields use a submit button — skip individual input events */
      if (e && e.target && e.target.type === 'number') return;
      if (e && e.target && e.target.tagName === 'SELECT') return;
      this._submitFilters();
    },

    onSortChange(val) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', val);
      url.searchParams.delete('page');
      this._fetchResults(url.toString(), true);
    },

    _submitFilters: debounce(function() {
      const forms = document.querySelectorAll('.coll-v2__flt-form');
      if (!forms.length) return;

      /* Build params from the first non-empty form (rail or drawer) */
      const url = new URL(window.location.pathname, window.location.origin);

      /* Preserve sort */
      const currentSort = new URLSearchParams(window.location.search).get('sort_by');
      if (currentSort) url.searchParams.set('sort_by', currentSort);

      /* Always reset to page 1 on filter change */
      url.searchParams.delete('page');

      forms.forEach(form => {
        const data = new FormData(form);
        for (const [key, value] of data.entries()) {
          if (key === 'sort_by') continue;
          if (value === '') continue; /* skip empty filter values */
          if (!url.searchParams.has(key)) {
            url.searchParams.append(key, value);
          }
        }
      });

      this._fetchResults(url.toString(), true);
    }, 80),

    async _fetchResults(url, pushState) {
      this.loading = true;

      /* Announce loading to screen readers */
      const resultsEl = document.getElementById(COLL_RESULTS_ID);
      if (resultsEl) resultsEl.setAttribute('aria-busy', 'true');

      try {
        const fetchUrl = url + (url.includes('?') ? '&' : '?') + 'section_id=' + COLL_SECTION_ID;
        const res = await fetch(fetchUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!res.ok) throw new Error('Network response was not ok');
        const html = await res.text();

        /* Parse and extract the results div from the returned section HTML */
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newResults = doc.getElementById(COLL_RESULTS_ID);
        const newCount   = doc.getElementById(COLL_COUNT_ID);

        if (newResults && resultsEl) {
          resultsEl.innerHTML = newResults.innerHTML;
        }

        /* Update result count */
        const countEl = document.getElementById(COLL_COUNT_ID);
        if (countEl && newCount) {
          countEl.textContent = newCount.textContent;
        }

        /* Update URL */
        if (pushState) {
          window.history.pushState({ collV2: true }, '', url);
        }

        /* Re-apply colour match if active */
        if (window._colourMatchHex) {
          ofColourMatch(window._colourMatchHex);
        }

        /* Scroll grid into view (softly) */
        if (resultsEl) {
          const top = resultsEl.getBoundingClientRect().top + window.scrollY - 120;
          if (window.scrollY > top) {
            window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          }
        }

        /* Re-apply v3 density if active */
        if (window.ofV3DensityRefresh) window.ofV3DensityRefresh();
        /* Re-init price sliders in refreshed HTML */
        if (window.ofInitPriceSlider) window.ofInitPriceSlider();

      } catch (err) {
        console.error('[collV2] Section render error:', err);
        /* Fallback: full navigation */
        window.location.href = url;
      } finally {
        this.loading = false;
        if (resultsEl) resultsEl.setAttribute('aria-busy', 'false');
      }
    }
  };
}

/* ── Register with Alpine.data so the component is guaranteed available
      before Alpine processes x-data="collV2" attributes ── */
document.addEventListener('alpine:init', function() {
  Alpine.data('collV2', collV2);
});

/* ── Handle browser back/forward ── */
window.addEventListener('popstate', function(e) {
  if (e.state && e.state.collV2) {
    const alpine = document.getElementById('coll-v2-page')?._x_dataStack?.[0];
    if (alpine) {
      alpine._fetchResults(window.location.href, false);
    } else {
      window.location.reload();
    }
  }
});

/* ================================================================
   SORT CHANGE (standalone, outside Alpine context for compat)
   ================================================================ */
function ofSort(val) {
  const url = new URL(window.location.href);
  url.searchParams.set('sort_by', val);
  url.searchParams.delete('page');
  window.location.href = url.toString();
}

/* ================================================================
   COLOUR ΔE — CIEDE2000 closest-colour mode
   ================================================================ */

/* sRGB → linear */
function srgbToLinear(c) {
  c /= 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/* sRGB → CIE XYZ (D65) */
function hexToXYZ(hex) {
  const r = srgbToLinear(parseInt(hex.slice(1, 3), 16));
  const g = srgbToLinear(parseInt(hex.slice(3, 5), 16));
  const b = srgbToLinear(parseInt(hex.slice(5, 7), 16));
  return {
    X: r * 0.4124564 + g * 0.3575761 + b * 0.1804375,
    Y: r * 0.2126729 + g * 0.7151522 + b * 0.0721750,
    Z: r * 0.0193339 + g * 0.1191920 + b * 0.9503041
  };
}

/* XYZ → CIE L*a*b* */
function xyzToLab(X, Y, Z) {
  const Xn = 0.95047, Yn = 1.00000, Zn = 1.08883;
  function f(t) { return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16/116; }
  const fx = f(X / Xn), fy = f(Y / Yn), fz = f(Z / Zn);
  return { L: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
}

/* CIEDE2000 ΔE */
function deltaE(hex1, hex2) {
  if (!hex1 || !hex2) return 999;
  try {
    const xyz1 = hexToXYZ(hex1), xyz2 = hexToXYZ(hex2);
    const lab1 = xyzToLab(xyz1.X, xyz1.Y, xyz1.Z);
    const lab2 = xyzToLab(xyz2.X, xyz2.Y, xyz2.Z);

    const L1 = lab1.L, a1 = lab1.a, b1 = lab1.b;
    const L2 = lab2.L, a2 = lab2.a, b2 = lab2.b;

    const kL = 1, kC = 1, kH = 1;
    const C1 = Math.sqrt(a1*a1 + b1*b1);
    const C2 = Math.sqrt(a2*a2 + b2*b2);
    const Cbar = (C1 + C2) / 2;
    const Cbar7 = Math.pow(Cbar, 7);
    const G = 0.5 * (1 - Math.sqrt(Cbar7 / (Cbar7 + 6103515625)));
    const a1p = a1 * (1 + G), a2p = a2 * (1 + G);
    const C1p = Math.sqrt(a1p*a1p + b1*b1);
    const C2p = Math.sqrt(a2p*a2p + b2*b2);
    const CbarP = (C1p + C2p) / 2;

    const h1p = (Math.atan2(b1, a1p) * 180 / Math.PI + 360) % 360;
    const h2p = (Math.atan2(b2, a2p) * 180 / Math.PI + 360) % 360;
    const dhp = C1p === 0 || C2p === 0 ? 0 :
                Math.abs(h2p - h1p) <= 180 ? h2p - h1p :
                h2p <= h1p ? h2p - h1p + 360 : h2p - h1p - 360;
    const dLp = L2 - L1;
    const dCp = C2p - C1p;
    const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin(dhp * Math.PI / 360);

    const HbarP = Math.abs(h1p - h2p) <= 180 || (C1p === 0 || C2p === 0) ?
                  (h1p + h2p) / 2 :
                  (h1p + h2p + (h1p + h2p < 360 ? 360 : -360)) / 2;
    const Lbar = (L1 + L2) / 2;
    const T = 1
            - 0.17 * Math.cos((HbarP - 30) * Math.PI / 180)
            + 0.24 * Math.cos(2 * HbarP * Math.PI / 180)
            + 0.32 * Math.cos((3 * HbarP + 6) * Math.PI / 180)
            - 0.20 * Math.cos((4 * HbarP - 63) * Math.PI / 180);
    const LbarM50sq = Math.pow(Lbar - 50, 2);
    const SL = 1 + 0.015 * LbarM50sq / Math.sqrt(20 + LbarM50sq);
    const SC = 1 + 0.045 * CbarP;
    const SH = 1 + 0.015 * CbarP * T;
    const CbarP7 = Math.pow(CbarP, 7);
    const RC = 2 * Math.sqrt(CbarP7 / (CbarP7 + 6103515625));
    const dTheta = 30 * Math.exp(-Math.pow((HbarP - 275) / 25, 2));
    const RT = -Math.sin(2 * dTheta * Math.PI / 180) * RC;

    return Math.sqrt(
      Math.pow(dLp / (kL * SL), 2) +
      Math.pow(dCp / (kC * SC), 2) +
      Math.pow(dHp / (kH * SH), 2) +
      RT * (dCp / (kC * SC)) * (dHp / (kH * SH))
    );
  } catch(e) {
    return 999;
  }
}

/* ── Public: ofColourMatch(hex) — called from colour picker ── */
window.ofColourMatch = function(targetHex) {
  window._colourMatchHex = targetHex;
  const cards = document.querySelectorAll('.pcard[data-colour-hex]');
  if (!cards.length) return;

  const THRESHOLD = 28;
  const results = [];

  cards.forEach(card => {
    const cardHex = card.dataset.colourHex;
    if (!cardHex) { results.push({ card, de: 999 }); return; }
    const de = deltaE(targetHex, cardHex);
    results.push({ card, de });
  });

  results.sort((a, b) => a.de - b.de);

  const grid = document.querySelector('.coll-v2__grid');
  if (!grid) return;

  results.forEach(({ card, de }, idx) => {
    const li = card.closest('li') || card;
    li.style.order = idx;
    if (de <= THRESHOLD) {
      card.classList.remove('colour-match-dim');
    } else {
      card.classList.add('colour-match-dim');
    }
  });

  const near = results.filter(r => r.de <= THRESHOLD).length;
  const statusEl = document.getElementById('flt-colour-status');
  if (statusEl) {
    statusEl.textContent = near > 0
      ? `${near} product${near === 1 ? '' : 's'} within this colour range`
      : 'No close matches — showing nearest available';
  }

  const clearBtn = document.getElementById('flt-colour-clear');
  if (clearBtn) clearBtn.style.display = 'block';

  const preview = document.getElementById('flt-colour-preview');
  const hexLabel = document.getElementById('flt-colour-hex');
  if (preview) preview.style.background = targetHex;
  if (hexLabel) hexLabel.textContent = targetHex;
};

/* ── Public: ofColourMatchClear() ── */
window.ofColourMatchClear = function() {
  window._colourMatchHex = null;

  document.querySelectorAll('.pcard').forEach(card => {
    card.classList.remove('colour-match-dim');
    const li = card.closest('li') || card;
    li.style.order = '';
  });

  const statusEl = document.getElementById('flt-colour-status');
  if (statusEl) statusEl.textContent = '';

  const clearBtn = document.getElementById('flt-colour-clear');
  if (clearBtn) clearBtn.style.display = 'none';
};

/* ── Colour picker input handler ── */
document.addEventListener('DOMContentLoaded', function() {
  const picker = document.getElementById('flt-colour-picker');
  if (!picker) return;
  picker.addEventListener('input', function() {
    const hex = this.value;
    const preview = document.getElementById('flt-colour-preview');
    const hexLabel = document.getElementById('flt-colour-hex');
    if (preview) preview.style.background = hex;
    if (hexLabel) hexLabel.textContent = hex;
  });
});

/* ================================================================
   FALLBACK PRICE FILTER (when S&D not configured)
   ================================================================ */
window.ofPriceFilterV2 = function() {
  const minEl = document.getElementById('flt-price-min-fb');
  const maxEl = document.getElementById('flt-price-max-fb');
  if (!minEl || !maxEl) return;
  const min = parseFloat(minEl.value) || 0;
  const max = parseFloat(maxEl.value) || Infinity;

  document.querySelectorAll('.pcard[data-price]').forEach(card => {
    const price = parseFloat(card.dataset.price || 0);
    const li = card.closest('li') || card.parentElement;
    li.style.display = (price >= min && price <= max) ? '' : 'none';
  });
};

/* ================================================================
   COLOUR FAMILY CLIENT-SIDE FILTER (fallback, no S&D)
   ================================================================ */
document.addEventListener('click', function(e) {
  const btn = e.target.closest('[data-colour-filter]');
  if (!btn) return;

  const family = btn.dataset.colourFilter;
  const isActive = btn.classList.contains('is-active');

  btn.classList.toggle('is-active', !isActive);
  btn.setAttribute('aria-pressed', (!isActive).toString());

  const activeFamilies = Array.from(
    document.querySelectorAll('[data-colour-filter].is-active')
  ).map(b => b.dataset.colourFilter);

  document.querySelectorAll('.pcard[data-colour-family]').forEach(card => {
    const cardFamily = card.dataset.colourFamily;
    const li = card.closest('li') || card.parentElement;
    if (!activeFamilies.length) {
      li.style.display = '';
    } else {
      li.style.display = activeFamilies.includes(cardFamily) ? '' : 'none';
    }
  });
});

/* ================================================================
   ADD TO CART
   ================================================================ */
if (typeof window.ofAddToCart === 'undefined') {
  window.ofAddToCart = function(variantId) {
    if (window.Alpine && Alpine.store && Alpine.store('cart')) {
      Alpine.store('cart').add(variantId, 1);
    }
  };
}
